import * as THREE from 'three';
import {CABINET_COMMON_COLOR_PART_IDS} from "../../../../../../constants";
import workspace from "../../../../../canvas/core/workspace";
import store from "../../../../../../../../store";
import {setSQFTActiveShapeId} from "../../../../../../../../actions/sqftMenuFunctions";
import {updateTallCabinetIn3D} from "../modelsLoader/cabinetInterior/tallCabinetUpdate3D";
import {
  CABINET_HIGHLIGHT_COLOR,
  SECTION_HIGHLIGHT_BOX_TAG,
  SECTION_HIGHLIGHT_COLOR,
  SECTION_LAYOUT_ID_TAG,
} from "../modelsLoader/cabinetInterior/tallCabinetSectionHighlightBoxes";

export default class ShapeContextMenuControls {
  constructor(threeScene, scene, camera, domElement, getSelectableGroups, onContextMenu) {
    this.threeScene    = threeScene;
    this.scene         = scene;
    this.camera        = camera;
    this.domElement    = domElement;
    this.getGroups     = getSelectableGroups;
    this.onContextMenu = onContextMenu;

    this.raycaster   = new THREE.Raycaster();
    this.mouse       = new THREE.Vector2();
    this.current     = null;
    this.highlighted = [];              // [{ mesh, originalColor }…]

    this.highlightColor = CABINET_HIGHLIGHT_COLOR;
    this.highlightEmissiveIntensity = 0.35;
    this.sectionHighlightColor = SECTION_HIGHLIGHT_COLOR;
    this.sectionLayoutIdTag = SECTION_LAYOUT_ID_TAG;
    this.sectionHighlightBoxTag = SECTION_HIGHLIGHT_BOX_TAG;
    this.dragThreshold = 5;
    this.pendingSectionDrag = null;
    this.activeSectionDrag = null;
    this.controlsWereEnabled = true;

    this.domElement.addEventListener('pointerdown',  this._onPointerDown,  false);
    this.domElement.addEventListener('pointermove',  this._onPointerMove,  false);
    this.domElement.addEventListener('pointerup',    this._onPointerUp,    false);
    this.domElement.addEventListener('pointercancel', this._onPointerCancel, false);
    this.domElement.addEventListener('pointerleave', this._onPointerLeave, false);
    this.domElement.addEventListener('contextmenu',   this._onContextMenu,  false);
  }

  dispose() {
    this.domElement.removeEventListener('pointerdown',   this._onPointerDown);
    this.domElement.removeEventListener('pointermove',   this._onPointerMove);
    this.domElement.removeEventListener('pointerup',     this._onPointerUp);
    this.domElement.removeEventListener('pointercancel', this._onPointerCancel);
    this.domElement.removeEventListener('pointerleave',  this._onPointerLeave);
    this.domElement.removeEventListener('contextmenu',    this._onContextMenu);
  }

  // ========== Highlight logic ==========
  _forEachMaterial(mesh, callback) {
    if (Array.isArray(mesh.material)) {
      mesh.material.forEach(callback);
      return;
    }

    callback(mesh.material);
  }

  _applyHighlightToMesh(mesh) {
    if (!mesh.isMesh || !mesh.material) {
      return;
    }

    if (mesh.userData[this.sectionHighlightBoxTag]) {
      this._forEachMaterial(mesh, (material) => {
        if (!material) {
          return;
        }

        this.highlighted.push({
          mesh,
          material,
          opacity: material.opacity,
          color: material.color && material.color.getHex(),
        });

        if (material.color) {
          material.color.setHex(this.sectionHighlightColor);
        }
        material.opacity = 0.22;
        material.needsUpdate = true;
      });
      return;
    }

    // Clone material on first highlight so shared GLB materials do not tint other meshes.
    if (!mesh.userData.hasOwnProperty('origMaterial')) {
      mesh.userData.origMaterial = mesh.material;
      mesh.material = Array.isArray(mesh.material)
        ? mesh.material.map((mat) => mat.clone())
        : mesh.material.clone();
    }

    this._forEachMaterial(mesh, (material) => {
      if (!material || !material.emissive) {
        return;
      }

      const origColor = material.emissive.getHex();
      if (origColor === this.highlightColor) {
        return;
      }

      this.highlighted.push({
        mesh,
        color: origColor,
        emissiveIntensity: material.emissiveIntensity,
        material,
      });
      material.emissive.setHex(this.highlightColor);
      if (material.emissiveIntensity !== undefined) {
        material.emissiveIntensity = this.highlightEmissiveIntensity;
      }
      material.needsUpdate = true;
    });
  }

  _applyHighlight(group) {
    this.highlighted = [];
    group.traverse(mesh => {
      if (mesh.isMesh && mesh.userData[this.sectionHighlightBoxTag]) {
        return;
      }

      this._applyHighlightToMesh(mesh);
    });
  }

  _applySectionHighlight(group, sectionId) {
    this.highlighted = [];
    const sectionHighlightBoxes = [];
    const sectionMeshes = [];

    group.traverse(mesh => {
      if (mesh.isMesh && mesh.userData[this.sectionLayoutIdTag] === sectionId) {
        if (mesh.userData[this.sectionHighlightBoxTag]) {
          sectionHighlightBoxes.push(mesh);
          return;
        }

        sectionMeshes.push(mesh);
      }
    });

    const meshesToHighlight = sectionHighlightBoxes.length ? sectionHighlightBoxes : sectionMeshes;
    meshesToHighlight.forEach((mesh) => this._applyHighlightToMesh(mesh));
  }

  _clearHighlight() {
    this.highlighted.forEach(({ material, color, opacity, emissiveIntensity }) => {
      if (material && opacity !== undefined) {
        material.opacity = opacity;
      }

      if (material && emissiveIntensity !== undefined) {
        material.emissiveIntensity = emissiveIntensity;
      }

      if (material && material.color && color !== undefined && (!material.emissive || opacity !== undefined)) {
        material.color.setHex(color);
      }

      if (material && material.emissive) {
        material.emissive.setHex(color);
      }

      if (material) {
        material.needsUpdate = true;
      }
    });
    this.highlighted = [];
  }

  _findCabinetGroup(object, groups) {
    let obj = object;

    while (obj.parent && !groups.includes(obj)) {
      obj = obj.parent;
    }

    return groups.includes(obj) ? obj : null;
  }

  _findSectionId(object, rootGroup) {
    let obj = object;

    while (obj && obj !== rootGroup.parent) {
      if (obj.userData && obj.userData[this.sectionLayoutIdTag]) {
        return obj.userData[this.sectionLayoutIdTag];
      }

      if (obj === rootGroup) {
        return null;
      }

      obj = obj.parent;
    }

    return null;
  }

  _setPointerFromEvent(event) {
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top)  / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
  }

  _getHitInfo(event) {
    this._setPointerFromEvent(event);

    const groups = this.getGroups();
    const hits = this.raycaster.intersectObjects(groups, true);

    if (!hits.length) {
      return null;
    }

    const group = this._findCabinetGroup(hits[0].object, groups);
    if (!group || !group.userData || !CABINET_COMMON_COLOR_PART_IDS.includes(group.userData.partId)) {
      return null;
    }

    return {
      group,
      object: hits[0].object,
      sectionId: this._findSectionId(hits[0].object, group),
    };
  }

  _setOrbitControlsEnabled(isEnabled) {
    if (this.threeScene && this.threeScene.controls) {
      this.threeScene.controls.enabled = isEnabled;
    }
  }

  _setCursor(cursor) {
    if (this.domElement.style.cursor !== cursor) {
      this.domElement.style.cursor = cursor;
    }
  }

  _resetSectionDrag() {
    this.pendingSectionDrag = null;
    this.activeSectionDrag = null;
    this._setCursor('');
    this._setOrbitControlsEnabled(this.controlsWereEnabled);
  }

  _canDragSectionsForShape(shapeId) {
    return shapeId && store.getState().sqftMenuState.sqftActiveShapeId === shapeId;
  }

  _highlightHitInfo(hitInfo, shouldHighlightSection = false) {
    if (!hitInfo || !hitInfo.group) {
      return;
    }

    const group = hitInfo.group;
    const sectionId = shouldHighlightSection ? hitInfo.sectionId : null;
    this._clearHighlight();
    this.current = sectionId ? `${group.uuid}:${sectionId}` : group;
    this.currentGroup = group;

    if (sectionId) {
      this._applySectionHighlight(group, sectionId);
      return;
    }

    this._applyHighlight(group);
  }

  _refreshActiveShape(shapeId) {
    store.dispatch(setSQFTActiveShapeId(null));
    setTimeout(() => {
      store.dispatch(setSQFTActiveShapeId(shapeId));
    }, 0);
  }

  _reorderLayoutSections(shapeId, fromSectionId, toSectionId) {
    if (!shapeId || !fromSectionId || !toSectionId || fromSectionId === toSectionId) {
      return false;
    }

    const shape = workspace.getShapeById(shapeId);
    if (!shape || !shape.shapeCabinet) {
      return false;
    }

    const layout = shape.shapeCabinet.getCabinetInteriorLayout();
    if (!layout || !Array.isArray(layout.sections)) {
      return false;
    }

    const fromIndex = layout.sections.findIndex((section) => section.id === fromSectionId);
    const toIndex = layout.sections.findIndex((section) => section.id === toSectionId);
    if (fromIndex < 0 || toIndex < 0) {
      return false;
    }

    const sections = [...layout.sections];
    const [movedSection] = sections.splice(fromIndex, 1);
    sections.splice(toIndex, 0, movedSection);

    const nextLayout = {
      ...layout,
      sections,
    };

    shape.shapeCabinet.setCabinetInteriorLayout(nextLayout);
    updateTallCabinetIn3D(shapeId, nextLayout);
    this._refreshActiveShape(shapeId);

    return true;
  }

  // ========== Event handlers ==========

  _onPointerDown = event => {
    if (event.button !== 0) {
      return;
    }

    const hitInfo = this._getHitInfo(event);
    if (
      !hitInfo
      || !hitInfo.sectionId
      || !hitInfo.group.userData.shapeId
      || !this._canDragSectionsForShape(hitInfo.group.userData.shapeId)
    ) {
      return;
    }

    this.controlsWereEnabled = !this.threeScene || !this.threeScene.controls || this.threeScene.controls.enabled;
    this._setOrbitControlsEnabled(false);
    this.pendingSectionDrag = {
      shapeId: hitInfo.group.userData.shapeId,
      sectionId: hitInfo.sectionId,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };

    if (this.domElement.setPointerCapture) {
      try {
        this.domElement.setPointerCapture(event.pointerId);
      } catch (e) {
        void e;
      }
    }
  }

  _onPointerMove = event => {
    if (this.pendingSectionDrag && !this.activeSectionDrag) {
      const distanceX = event.clientX - this.pendingSectionDrag.startX;
      const distanceY = event.clientY - this.pendingSectionDrag.startY;
      const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

      if (distance >= this.dragThreshold) {
        this.activeSectionDrag = this.pendingSectionDrag;
        this._setCursor('grabbing');
      }
    }

    if (this.activeSectionDrag) {
      event.preventDefault();
    }

    const hitInfo = this._getHitInfo(event);

    if (hitInfo) {
      const obj = hitInfo.group;
      const sectionId = this._canDragSectionsForShape(obj.userData.shapeId) ? hitInfo.sectionId : null;
      const nextCurrent = sectionId ? `${obj.uuid}:${sectionId}` : obj;

      if (!this.activeSectionDrag) {
        this._setCursor(sectionId ? 'grab' : '');
      }

      if (obj && nextCurrent !== this.current && obj.userData && CABINET_COMMON_COLOR_PART_IDS.includes(obj.userData.partId)) {
        this._clearHighlight();
        this.current = nextCurrent;
        this.currentGroup = obj;

        if (sectionId) {
          this._applySectionHighlight(obj, sectionId);
        } else {
          this._applyHighlight(obj);
        }
      } else if (!obj || !obj.userData || !CABINET_COMMON_COLOR_PART_IDS.includes(obj.userData.partId)) {
        this._clearHighlight();
        this.current = null;
        this.currentGroup = null;
        this._setCursor('');
      }
    } else {
      this._clearHighlight();
      this.current = null;
      this.currentGroup = null;
      this._setCursor('');
    }
  }

  _onPointerUp = event => {
    if (!this.pendingSectionDrag) {
      return;
    }

    const drag = this.activeSectionDrag;
    const hitInfo = this._getHitInfo(event);

    if (drag && hitInfo && hitInfo.group.userData.shapeId === drag.shapeId) {
      const didReorder = this._reorderLayoutSections(drag.shapeId, drag.sectionId, hitInfo.sectionId);
      if (didReorder) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    if (this.domElement.releasePointerCapture) {
      try {
        this.domElement.releasePointerCapture(event.pointerId);
      } catch (e) {
        void e;
      }
    }

    this._resetSectionDrag();
  }

  _onPointerCancel = event => {
    if (this.domElement.releasePointerCapture) {
      try {
        this.domElement.releasePointerCapture(event.pointerId);
      } catch (e) {
        void e;
      }
    }

    this._resetSectionDrag();
  }

  _onPointerLeave = () => {
    if (this.activeSectionDrag) {
      return;
    }

    this._clearHighlight();
    this.current = null;
    this.currentGroup = null;
    this._setCursor('');
  }

  _onContextMenu = event => {
    const hitInfo = this._getHitInfo(event);
    const group = hitInfo ? hitInfo.group : this.currentGroup;
    if (!group) return;

    this._highlightHitInfo(hitInfo, true);
    event.preventDefault();
    this.onContextMenu(group, event);
  }
}
